import React, { useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import CircularProgress from '@mui/material/CircularProgress'
import type { ToolResultDetail } from '@/core/models/medconnect-agent.model'
import { normalizeUtf8 } from '@/utils/encoding'
import MarkdownProcessor from './MarkdownProcessor'

/** Orchestrator meta-tools (see apiGateway medconnect-agent/orchestrator). */
export const LOAD_TOOLS_TOOL_NAME = 'load_tools'
export const DELEGATE_TOOL_NAME = 'delegate'

export const isOrchestratorTool = (toolName?: string): boolean =>
    toolName === LOAD_TOOLS_TOOL_NAME || toolName === DELEGATE_TOOL_NAME

// ─── Shared parsing ───────────────────────────────────────────────────────────

function parseJsonResult(result: unknown): any {
    if (result === undefined || result === null) return undefined
    if (typeof result !== 'string') return result
    try {
        return JSON.parse(normalizeUtf8(result))
    } catch {
        return undefined
    }
}

interface LoadedGroup {
    group: string
    tools: string[]
}

/** `load_tools` args = `{ groups }`; result = `{ loaded: [{ group, tools, guidance }], rejected, hint }`. */
export function parseLoadedTools(toolInvocation: ToolResultDetail): { groups: string[]; loaded: LoadedGroup[]; rejected: string[]; pending: boolean } {
    const requested: string[] = Array.isArray(toolInvocation.tool_args?.groups) ? toolInvocation.tool_args.groups : []
    const result = parseJsonResult(toolInvocation.result)
    const loaded: LoadedGroup[] = Array.isArray(result?.loaded)
        ? result.loaded.map((g: any) => ({ group: String(g.group), tools: Array.isArray(g.tools) ? g.tools.map(String) : [] }))
        : []
    const rejected: string[] = Array.isArray(result?.rejected) ? result.rejected.map(String) : []
    const groups = loaded.length > 0 ? loaded.map((g) => g.group) : requested
    return { groups, loaded, rejected, pending: toolInvocation.result === undefined }
}

export interface DelegateOutcome {
    outcome: string
    actions: Array<{ tool: string; summary: string; link?: string }>
    entities: Record<string, string>
    artifacts: Array<{ type: string; id?: string; url?: string }>
    openQuestions: string[]
    status: 'done' | 'partial' | 'failed'
}

/** `delegate` result = `SubagentOutcome` JSON. Falls back to a plain-text outcome. */
export function parseDelegateOutcome(toolInvocation: ToolResultDetail): DelegateOutcome | undefined {
    if (toolInvocation.result === undefined) return undefined
    const parsed = parseJsonResult(toolInvocation.result)
    if (!parsed || typeof parsed !== 'object') {
        const text = typeof toolInvocation.result === 'string' ? normalizeUtf8(toolInvocation.result) : JSON.stringify(toolInvocation.result)
        return { outcome: text, actions: [], entities: {}, artifacts: [], openQuestions: [], status: 'done' }
    }
    return {
        outcome: typeof parsed.outcome === 'string' ? parsed.outcome : '',
        actions: Array.isArray(parsed.actions) ? parsed.actions : [],
        entities: parsed.entities && typeof parsed.entities === 'object' ? parsed.entities : {},
        artifacts: Array.isArray(parsed.artifacts) ? parsed.artifacts : [],
        openQuestions: Array.isArray(parsed.openQuestions) ? parsed.openQuestions : [],
        status: parsed.status === 'partial' || parsed.status === 'failed' ? parsed.status : 'done',
    }
}

// ─── load_tools ───────────────────────────────────────────────────────────────

/** Collapsed chip: "Loaded tools: patient, consultation" — expands to the tool names. */
export const LoadedToolsChip: React.FC<{ toolInvocation: ToolResultDetail }> = ({ toolInvocation }) => {
    const { t } = useTranslation()
    const [expanded, setExpanded] = useState(false)
    const { groups, loaded, rejected, pending } = useMemo(() => parseLoadedTools(toolInvocation), [toolInvocation])

    const label = t('page.medconnectAgent.chatInterface.loadedTools', { groups: groups.join(', ') || '—' })
    const canExpand = loaded.some((g) => g.tools.length > 0) || rejected.length > 0

    return (
        <Box sx={{ my: 0.5 }}>
            <Chip
                size="small"
                variant="outlined"
                icon={pending ? <CircularProgress size={12} color="inherit" /> : <ExtensionOutlinedIcon sx={{ fontSize: 16 }} />}
                label={label}
                onClick={canExpand ? () => setExpanded((v) => !v) : undefined}
                sx={{ maxWidth: '100%', color: 'text.secondary', borderColor: 'divider', cursor: canExpand ? 'pointer' : 'default' }}
            />
            <Collapse in={expanded && canExpand} unmountOnExit>
                <Box sx={{ pl: 1.5, mt: 0.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                    {loaded.map((g) => (
                        <Typography key={g.group} variant="caption" sx={{ display: 'block', color: 'text.secondary', fontFamily: 'monospace' }}>
                            {g.group}: {g.tools.join(', ')}
                        </Typography>
                    ))}
                    {rejected.length > 0 && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'error.main' }}>
                            ✕ {rejected.join(', ')}
                        </Typography>
                    )}
                </Box>
            </Collapse>
        </Box>
    )
}

// ─── delegate ─────────────────────────────────────────────────────────────────

const isInternalLink = (href: string) => href.startsWith('/')

const ActionLink: React.FC<{ link: string; children: React.ReactNode }> = ({ link, children }) =>
    isInternalLink(link)
        ? <Link component={RouterLink} to={link} underline="hover">{children}</Link>
        : <Link href={link} target="_blank" rel="noopener noreferrer" underline="hover">{children}</Link>

/** Delegated sub-task outcome: markdown of `outcome` + the performed actions as links. */
export const DelegateOutcomeDisplay: React.FC<{ toolInvocation: ToolResultDetail; compact?: boolean }> = ({ toolInvocation, compact = false }) => {
    const { t } = useTranslation()
    const outcome = useMemo(() => parseDelegateOutcome(toolInvocation), [toolInvocation])
    const task: string | undefined = typeof toolInvocation.tool_args?.task === 'string' ? toolInvocation.tool_args.task : undefined
    const groups: string[] = Array.isArray(toolInvocation.tool_args?.groups) ? toolInvocation.tool_args.groups : []

    const status = outcome?.status
    const statusIcon = !outcome
        ? <CircularProgress size={12} color="inherit" />
        : status === 'failed'
            ? <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
            : status === 'partial'
                ? <HourglassEmptyIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                : <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />

    const title = status === 'failed'
        ? t('page.medconnectAgent.chatInterface.delegateFailed')
        : t('page.medconnectAgent.chatInterface.delegateOutcome')

    return (
        <Box
            sx={{
                my: 0.75,
                p: compact ? 1 : 1.5,
                borderRadius: '8px',
                border: '1px solid',
                borderColor: status === 'failed' ? 'error.light' : 'divider',
                backgroundColor: 'action.hover',
            }}
        >
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: outcome ? 0.5 : 0, minWidth: 0 }}>
                <AccountTreeOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    {title}
                </Typography>
                {groups.length > 0 && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace' }} noWrap>
                        [{groups.join(', ')}]
                    </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                {statusIcon}
            </Stack>
            {!outcome && task && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {task}
                </Typography>
            )}
            {outcome?.outcome && <MarkdownProcessor content={outcome.outcome} compact />}
            {outcome && outcome.actions.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {t('page.medconnectAgent.chatInterface.delegateActions')}
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {outcome.actions.map((action, idx) => (
                            <Typography component="li" variant="body2" key={`${action.tool}-${idx}`}>
                                {action.link ? <ActionLink link={action.link}>{action.summary || action.tool}</ActionLink> : (action.summary || action.tool)}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            )}
            {outcome && outcome.openQuestions.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                        {t('page.medconnectAgent.chatInterface.openQuestions')}
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {outcome.openQuestions.map((q, idx) => (
                            <Typography component="li" variant="body2" key={idx}>{q}</Typography>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    )
}

/** Renders the right component for an orchestrator meta-tool; `null` for regular tools. */
export const OrchestratorToolDisplay: React.FC<{ toolInvocation: ToolResultDetail; compact?: boolean }> = ({ toolInvocation, compact }) => {
    if (toolInvocation.tool_name === LOAD_TOOLS_TOOL_NAME) return <LoadedToolsChip toolInvocation={toolInvocation} />
    if (toolInvocation.tool_name === DELEGATE_TOOL_NAME) return <DelegateOutcomeDisplay toolInvocation={toolInvocation} compact={compact} />
    return null
}

export default OrchestratorToolDisplay
